import React, { useCallback, useEffect, useState } from 'react';
import { mcpAccessService } from '../../services/api';
import { McpAccessEntry, McpAccessShape } from '../../types';
import { formatDateTime } from '../../utils/datetime';
import ScopeFilter, { ScopeSelection } from '../ScopeFilter';

// Two readings of the same log.
//
// "Calls" is the chronological one: who asked what, when, and what it cost.
// "Query shapes" is the one that pays for the log's existence — calls grouped
// by (tool, set of filters used), ordered by total work thrown away. A shape
// near the top matched far more rows than it returned, repeatedly, which is
// precisely the signature of a query the index does not serve.

type View = 'log' | 'shapes';

const nf = new Intl.NumberFormat();

// Rows examined per row returned. 1 is perfect. The thresholds are for
// drawing the eye, not a rule: anything under ~5 is unremarkable, and a
// three-digit ratio on a frequently-called shape is worth an index.
function wasteStyle(ratio: number | null): React.CSSProperties {
  if (ratio === null) return {};
  if (ratio >= 100) return { color: '#c0392b', fontWeight: 600 };
  if (ratio >= 10) return { color: '#b9770e' };
  return {};
}

const McpAccessTable: React.FC = () => {
  const [view, setView] = useState<View>('shapes');
  const [scope, setScope] = useState<ScopeSelection>({});
  const [entries, setEntries] = useState<McpAccessEntry[]>([]);
  const [shapes, setShapes] = useState<McpAccessShape[]>([]);
  const [paramsVisible, setParamsVisible] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { territory: scope.territory, domain: scope.domain };
      if (view === 'shapes') {
        setShapes(await mcpAccessService.shapes(params));
      } else {
        const res = await mcpAccessService.list({ ...params, page, limit: 50 });
        setEntries(res.entries);
        setParamsVisible(res.params_visible);
        setTotal(res.pagination?.total_count ?? res.entries.length);
      }
    } catch (e) {
      setError('Could not load the MCP access log');
    } finally {
      setLoading(false);
    }
  }, [view, page, scope.territory, scope.domain]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="settings-panel">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
        <h2 style={{ marginRight: 'auto' }}>MCP Access</h2>
        <nav className="settings-subnav">
          <button
            className={`nav-button ${view === 'shapes' ? 'active' : ''}`}
            onClick={() => setView('shapes')}
          >
            Query shapes
          </button>
          <button
            className={`nav-button ${view === 'log' ? 'active' : ''}`}
            onClick={() => { setView('log'); setPage(1); }}
          >
            Calls
          </button>
        </nav>
      </div>

      <ScopeFilter value={scope} onChange={s => { setScope(s); setPage(1); }} />

      {error && <div className="error">{error}</div>}
      {loading && <div className="loading">Loading…</div>}

      {view === 'shapes' ? (
        <>
          <p className="small">
            Every MCP call grouped by which filters it used — the query's shape.
            <strong> Waste</strong> is rows examined per row returned; a high
            waste on a shape called often is the case for a better index.
            Ordered by total rows thrown away.
          </p>
          <div className="table-container">
          <table className="wide-table">
            <thead>
              <tr>
                <th>Tool</th>
                <th>Filters used</th>
                <th style={{ textAlign: 'right' }}>Calls</th>
                <th style={{ textAlign: 'right' }}>Matched</th>
                <th style={{ textAlign: 'right' }}>Returned</th>
                <th style={{ textAlign: 'right' }}>Waste</th>
                <th style={{ textAlign: 'right' }}>Avg ms</th>
                <th style={{ textAlign: 'right' }}>Max ms</th>
                <th style={{ textAlign: 'right' }}>Query / Store</th>
                <th>Path</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {shapes.map((s, i) => (
                <tr key={`${s.tool}/${s.filter_keys}/${i}`}>
                  <td className="monospace">{s.tool || '—'}</td>
                  <td className="monospace small wrap">{s.filter_keys || '—'}</td>
                  <td style={{ textAlign: 'right' }}>{nf.format(s.calls)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {s.avg_matched === null ? '—' : nf.format(Math.round(s.avg_matched))}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {s.avg_results === null ? '—' : nf.format(Math.round(s.avg_results))}
                  </td>
                  <td style={{ textAlign: 'right', ...wasteStyle(s.waste_ratio) }}>
                    {s.waste_ratio === null ? '—' : `${nf.format(s.waste_ratio)}×`}
                  </td>
                  <td style={{ textAlign: 'right' }}>{s.avg_ms ?? '—'}</td>
                  <td style={{ textAlign: 'right' }}>{s.max_ms ?? '—'}</td>
                  <td style={{ textAlign: 'right' }} className="small">
                    {s.avg_query_ms ?? 0} / {s.avg_storage_ms ?? 0}
                  </td>
                  <td className="monospace small">{s.index_paths || '—'}</td>
                  <td className="small">{formatDateTime(s.last_at)}</td>
                </tr>
              ))}
              {!loading && shapes.length === 0 && (
                <tr><td colSpan={11}>No MCP calls recorded yet.</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </>
      ) : (
        <>
          <p className="small">
            {nf.format(total)} recorded call{total === 1 ? '' : 's'}.
            {paramsVisible
              ? ' Click a row to see the exact arguments.'
              : ' Search parameters are shown to Super Users only — they can contain the names and numbers of recorded parties.'}
            {' '}Entries are kept for 90 days.
          </p>
          <div className="table-container">
          <table className="wide-table">
            <thead>
              <tr>
                <th>When</th>
                <th>App</th>
                <th>Tool</th>
                <th>Scope searched</th>
                <th>Search</th>
                <th style={{ textAlign: 'right' }}>Returned / matched</th>
                <th style={{ textAlign: 'right' }}>ms</th>
                <th>Path</th>
                <th>Status</th>
                <th>From</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <React.Fragment key={e.id}>
                  <tr
                    onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                    style={{ cursor: paramsVisible ? 'pointer' : 'default' }}
                  >
                    <td className="small">{formatDateTime(e.created_at)}</td>
                    <td>{e.app_name || '—'}</td>
                    <td className="monospace">{e.tool || e.method}</td>
                    <td className="monospace small">
                      {e.territory || '—'}
                      {e.domain && e.domain !== '*' ? ` / ${e.domain}` : ''}
                    </td>
                    <td className="small">
                      {e.search_text
                        ? <span className="monospace">{e.search_text}</span>
                        : <span style={{ opacity: 0.5 }}>{e.filter_keys || '—'}</span>}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {e.result_count === null ? '—'
                        : `${nf.format(e.result_count)} / ${e.total_matched === null ? '?' : nf.format(e.total_matched)}`}
                    </td>
                    <td style={{ textAlign: 'right' }}>{e.duration_ms ?? '—'}</td>
                    <td className="monospace small">{e.index_path || '—'}</td>
                    <td>
                      {e.status === 'ok'
                        ? <span style={{ opacity: 0.6 }}>ok</span>
                        : <span style={{ color: '#c0392b' }}>{e.error_code || e.status}</span>}
                    </td>
                    <td className="monospace small">{e.remote_ip || '—'}</td>
                  </tr>
                  {expanded === e.id && e.params && (
                    <tr>
                      <td colSpan={10} className="wrap">
                        <pre className="monospace small" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(e.params, null, 2)}
                        </pre>
                        <div className="small" style={{ opacity: 0.7 }}>
                          key scope: {e.scope_territory} / {e.scope_domain}
                          {' · '}query {e.query_ms ?? 0} ms, storage {e.storage_ms ?? 0} ms
                          {e.limit_requested !== null && ` · limit ${e.limit_requested}`}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {!loading && entries.length === 0 && (
                <tr><td colSpan={10}>No MCP calls recorded yet.</td></tr>
              )}
            </tbody>
          </table>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '10px' }}>
            <button disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}>Previous</button>
            <span className="small">Page {page}</span>
            <button disabled={page * 50 >= total || loading} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        </>
      )}
    </div>
  );
};

export default McpAccessTable;
